import { Toast } from 'antd-mobile';
import axios, { AxiosError } from 'axios';
import { isApp } from '../utils/EnvUtils';
import { JsBridge } from '../utils/JsBridge'; // 从 index.tsx 导入 tokenPromise
import { createDecryptFunction } from '../utils/aesDecrypt';
import {
  DEV_API_BASE_PROD,
  DEV_API_BASE_TEST,
  DEV_ENV_ROC_HOME,
  findDevEnvPresetByHostname,
} from '../config/devEnv';
import { getUrlParam, safeLocalStorageGet, safeLocalStorageSet ,toBearerAuthorization} from '../utils/common';

/** 开发版 API 覆盖（与小程序 `dev_api_base_url_override` 语义一致，便于 H5 直连局域网后端） */
const DEV_API_BASE_OVERRIDE_STORAGE_KEY = 'dev_api_base_url_override';

/**
 * 去掉 API 根地址末尾斜杠。
 *
 * @param url 原始地址
 */
function stripTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

/**
 * 从 URL query 解析 `apiBase`（小程序 web-view 调试时与端内 API 对齐）。
 *
 * @param paramName query 参数名，默认 `apiBase`
 */
function parseApiBaseFromUrlQuery(paramName = 'apiBase'): string {
  if (typeof window === 'undefined') {
    return '';
  }
  const raw = getUrlParam(paramName).trim();
  if (!raw) {
    return '';
  }
  try {
    const decoded = decodeURIComponent(raw);
    if (/^https?:\/\//i.test(decoded)) {
      return stripTrailingSlash(decoded);
    }
  } catch {
    /* ignore */
  }
  return '';
}

/**
 * 解析当前页应使用的 API 根地址。
 * 优先级：URL `apiBase` → localStorage 覆盖 → `REACT_APP_APPLICATION_API_BASE` → `.env` 的 `BASE_URL`
 * → 局域网 hostname 预设 → 生产域名 → 默认测试 `testy6`。
 *
 * 家里调试可设：`localStorage.setItem('dev_api_base_url_override', DEV_ENV_ROC_HOME.apiBaseUrl)`
 * 或使用 {@link DEV_ENV_ROC_HOME} 的 `apiBaseUrl`。
 */
export function resolveApiBaseUrl(): string {
  const fromQuery = parseApiBaseFromUrlQuery('apiBase');
  if (fromQuery) {
    return fromQuery;
  }

  if (typeof window !== 'undefined') {
    const fromStorage = safeLocalStorageGet(DEV_API_BASE_OVERRIDE_STORAGE_KEY)?.trim();
    if (fromStorage) {
      return stripTrailingSlash(fromStorage);
    }
  }

  const fromReactAppEnv = process.env.REACT_APP_APPLICATION_API_BASE?.trim();
  if (fromReactAppEnv) {
    return stripTrailingSlash(fromReactAppEnv);
  }

  const fromBaseUrlEnv = process.env.BASE_URL?.trim();
  if (fromBaseUrlEnv && /^https?:\/\//i.test(fromBaseUrlEnv)) {
    return stripTrailingSlash(fromBaseUrlEnv);
  }

  if (typeof window !== 'undefined') {
    const preset = findDevEnvPresetByHostname(window.location.hostname);
    if (preset) {
      return preset.apiBaseUrl;
    }
    const host = window.location.hostname;
    if (host === 'sduck.net' || host === 'xsm.sduck.net') {
      return DEV_API_BASE_PROD;
    }
  }

  return DEV_API_BASE_TEST;
}
// 家里整机：DEV_API_PROXY_TARGET=<DEV_ENV_ROC_HOME.apiBaseUrl> npm start（见 devEnv.ts `roc(家)`）
// 或：localStorage dev_api_base_url_override = DEV_ENV_ROC_HOME.apiBaseUrl

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  msg?: string;
  result: string;
}

/**
 * 获取当前环境 API 根地址（与 axiosInstance.baseURL 一致）。
 */
export function getApiBaseHost(): string {
  return resolveApiBaseUrl();
}

export function getWebBaseHost(): string {
  console.log('window.location.origin', window.location.origin);
  return window.location.origin;
  // return "https://sduck.net";
  // return "http://192.168.31.236:8000";
}

export const axiosInstance = axios.create({
  baseURL: resolveApiBaseUrl(),
  transformResponse: [
    (data: string) => {
      // 将超过 JS 安全精度的长整数转为字符串，避免雪花 ID 精度丢失
      try {
        return JSON.parse(data.replace(/:(\s*)(\d{16,})/g, ':$1"$2"'));
      } catch {
        return data;
      }
    },
  ],
});

// 添加请求拦截器来动态设置 token，并确保等待 token
axiosInstance.interceptors.request.use(
  async function (config) {
    config.baseURL = resolveApiBaseUrl();

    // ---- URL 参数（小程序 web-view 场景，最高优先级）----
    const tokenFromUrl = typeof window !== 'undefined' ? getUrlParam('token').trim() : '';
    const appIdFromUrl = typeof window !== 'undefined' ? getUrlParam('appId').trim() : '';
    if (tokenFromUrl) {
      safeLocalStorageSet('ticket_token', tokenFromUrl);
    }
    if (appIdFromUrl) {
      safeLocalStorageSet('ticket_appId', appIdFromUrl);
    }

    // ---- Token 提取：App 内 UserAgent 优先，localStorage 兜底 ----
    let resolvedToken: string | undefined;

    // ① App 内：UserAgent token（Flutter 每次创建 WebView 实时注入，永远最新）
    const authMatch = navigator.userAgent.match(/Authorization_Bearer\s([^\s]+)/);
    if (authMatch && authMatch[1]) {
      resolvedToken = authMatch[1];
      // 同步更新 localStorage，避免后续请求读到旧值
      safeLocalStorageSet('ticket_token', authMatch[1]);
    }

    // ② 兜底：localStorage ticket_token（非 App 场景）
    if (!resolvedToken) {
      resolvedToken = tokenFromUrl || safeLocalStorageGet('ticket_token') || undefined;
    }

    const tokenAuth = toBearerAuthorization(resolvedToken);
    if (tokenAuth) {
      config.headers.authorization = tokenAuth;
    }

    // ---- AppId 提取 ----
    const ticketAppId = appIdFromUrl || safeLocalStorageGet('ticket_appId');
    let appId: string | undefined = ticketAppId || undefined;

    if (ticketAppId) {
      config.headers['X-App-Id'] = ticketAppId;
    }

    // 如果没有 ticket appId，则从 userAgent 中获取
    if (!ticketAppId) {
      const appIdMatch = navigator.userAgent.match(/X-App-Id_([^\s]+)/);
      if (appIdMatch && appIdMatch[1]) {
        appId = appIdMatch[1];
        config.headers['X-App-Id'] = appId;
      }
    }
    
    // 将 appId 保存到 config 的自定义字段中，以便在响应拦截器中可靠地获取
    // 使用 metadata 字段（如果不存在则创建）
    if (!(config as any).metadata) {
      (config as any).metadata = {};
    }
    (config as any).metadata.appId = appId;
    
    //mock
    // config.headers.authorization =
    //   'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1aWQiOjEzNDgzNTEsIm1pZCI6NDI3LCJleHAiOjE3NjE4NzY0NTJ9.LDtJizrchvaMkx8ouLT75QhddUBD6KYI6Tp6LJKw8hE';
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

const _nativeDecrypt = (data: string) => {
  return new Promise(resolve => {
    JsBridge.nativeDecrypt(data, (args: string) => {
      console.log('args', args);
      resolve(args);
    });
  });
};

// 创建 AES 解密函数
const aesDecryptFn = createDecryptFunction();

/**
 * 解密数据
 * @param data 要解密的数据（字符串类型）
 * @param appId X-App-Id，用于判断使用哪种解密方式
 * @param url 请求 URL，用于 AES 解密时选择密钥
 */
async function decryptData(data: string, appId?: string, url?: string): Promise<any> {
  console.log('[解密函数] decryptData 调用，appId:', appId, '数据类型:', typeof data, '长度:', data.length);
  
  try {
    // 如果 X-App-Id 为 'uro25b6xwubbvfpi'，使用 AES 解密
    if (appId === 'uro25b6xwubbvfpi') {
      console.log('[解密函数] 使用 AES 解密');
      const decrypted = aesDecryptFn(data, url || '');
      console.log('[解密函数] AES 解密完成，结果类型:', typeof decrypted);
      return decrypted;
    } else {
      // 否则使用原生解密
      console.log('[解密函数] 使用原生解密');
      const decrypted = await _nativeDecrypt(data);
      console.log('[解密函数] 原生解密完成，结果类型:', typeof decrypted);
      
      // 如果解密后是字符串，尝试解析为 JSON
      if (typeof decrypted === 'string') {
        try {
          const parsed = JSON.parse(decrypted);
          console.log('[解密函数] JSON 解析成功');
          return parsed;
        } catch {
          console.log('[解密函数] JSON 解析失败，返回原始字符串');
          return decrypted;
        }
      }
      return decrypted;
    }
  } catch (error) {
    console.error('[解密函数] 解密失败:', error);
    return data;
  }
}

axiosInstance.interceptors.response.use(
  async function (response) {
    // 根据 eType 判断是否需要解密，eType === 1 表示加密
    if (response.data) {
      // 获取请求时的 X-App-Id
      // 优先从 config.metadata 中获取（在请求拦截器中保存的）
      // 如果不存在，则尝试从 headers 中获取，最后从 localStorage 获取
      const appId = (response?.config as any)?.metadata?.appId ||
                    response?.config?.headers?.['X-App-Id'] || 
                    response?.config?.headers?.['x-app-id'] ||
                    safeLocalStorageGet('ticket_appId');
      const requestUrl = response?.config?.url || '';
      
      console.log('[解密判断] 获取到的 appId:', appId, '来源:', {
        metadata: (response?.config as any)?.metadata?.appId,
        headersXAppId: response?.config?.headers?.['X-App-Id'],
        headersXappId: response?.config?.headers?.['x-app-id'],
        localStorage: safeLocalStorageGet('ticket_appId')
      });
      
      // 如果响应数据是对象，检查 eType 字段
      if (typeof response.data === 'object' && response.data !== null) {
        const apiResponse = response.data as any;
        console.log('[解密判断] 响应数据是对象，eType:', apiResponse.eType, 'data类型:', typeof apiResponse.data, 'appId:', appId);
        
        // 检查 eType 字段，1 表示加密（根据后端 Response.java 中的 EType.ENCRYPT.getType()）
        if (apiResponse.eType === 1 && apiResponse.data && typeof apiResponse.data === 'string') {
          console.log('[解密判断] 检测到加密数据（eType === 1），开始解密 data 字段');
          apiResponse.data = await decryptData(apiResponse.data, appId, requestUrl);
          console.log('[解密判断] data 字段解密完成');
        } else {
          console.log('[解密判断] 未检测到加密数据（eType !== 1 或 data 不是字符串），跳过解密');
        }
      }
      // 如果响应数据是字符串，且 eType 为 1，则解密（这种情况较少见）
      else if (typeof response.data === 'string') {
        // 注意：如果响应直接是字符串，通常不会有 eType 字段
        // 这种情况可能需要根据实际情况调整
        console.log('[解密判断] 响应数据是字符串，但无法判断 eType，跳过解密');
      }
    }
    
    return response;
  },
  function (error) {
    console.error(error.response?.status, error.response?.config?.url);
    if (error.response?.status === 401) {
      JsBridge.gotoLogin({ sourceFrom: 'h5' });
      return;
    }
    // if (error.response?.status === 400) {
    //   return error.response
    // }
    return Promise.reject(error);
  }
);

/**
 * 发起 GET 基础请求，包含自己服务端约定格式数据的基本逻辑处理，第三方的请求不要调用该基础请求
 * @template T - 响应数据的类型
 * @param {string} url 请求的 URL
 * @param {Record<string, any>} params 请求的参数
 * @returns {Promise<{success: boolean, data?: T, msg?: string, code?: number}>} 返回一个包含结果的 Promise 对象
 */
export async function requestGet<T>(
  url: string,
  params: Record<string, any>
): Promise<{
  success: boolean;
  data?: T;
  msg?: string;
  result?: string;
  code?: number;
}> {
  try {
    const response = await axiosInstance.get(url, { params: params });

    if (response.status === 200 && response.data?.result === 'SUCCESS') {
      return {
        success: true,
        data: response.data.data,
        msg: response.data?.msg ?? '请求成功',
        code: response.status,
        result: response.data?.result,
      };
    } else {
      return {
        success: false,
        msg: response.data?.msg ?? '请求失败',
        code: response.status,
        result: response.data?.result,
      };
    }
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      // 服务器返回了响应，但状态码不是 2xx
      return {
        success: false,
        msg: error?.response?.data?.msg ?? '请求失败',
        code: error?.response?.status ?? 500,
        result: error?.response?.data?.result,
      };
    } else {
      // 请求已发出，但没有收到响应
      return {
        success: false,
        msg: '网络错误，请检查网络连接',
        code: 500,
        result: 'ERROR',
      };
    }
  }
}

// export function requestPost<T>(
//   url: string,
//   data?: any
// ): Promise<ApiResponse<T>> {
//   return axiosInstance.post(url, data).then((response) => response.data);
// }

export async function requestPost<T>(
  url: string,
  data?: any,
  params?: Record<string, any>
): Promise<{
  success: boolean;
  data?: T;
  msg?: string;
  result?: string;
  code?: number;
}> {
  try {
    const response = await axiosInstance.post(url, data, { params: params });
    if (response.status === 200 && response.data?.result === 'SUCCESS') {
      return {
        success: true,
        data: response.data.data,
        msg: response.data?.msg ?? '请求成功',
        code: response.status,
        result: response.data?.result,
      };
    } else {
      return {
        success: false,
        msg: response.data?.msg ?? '请求失败',
        code: response.status,
        result: response.data?.result,
      };
    }
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      // 服务器返回了响应，但状态码不是 2xx
      return {
        success: false,
        msg: error?.response?.data?.msg ?? '请求失败',
        code: error?.response?.status ?? 500,
        result: error?.response?.data?.result,
      };
    } else {
      // 请求已发出，但没有收到响应
      return {
        success: false,
        msg: '网络错误，请检查网络连接',
        code: 500,
        result: 'ERROR',
      };
    }
  }
}

/**
 * 发起 DELETE 基础请求，响应格式与 {@link requestPost} 一致。
 *
 * @param url 请求路径
 */
export async function requestDelete<T>(
  url: string,
): Promise<{
  success: boolean;
  data?: T;
  msg?: string;
  result?: string;
  code?: number;
}> {
  try {
    const response = await axiosInstance.delete(url);
    if (response.status === 200 && response.data?.result === 'SUCCESS') {
      return {
        success: true,
        data: response.data.data,
        msg: response.data?.msg ?? '请求成功',
        code: response.status,
        result: response.data?.result,
      };
    }
    return {
      success: false,
      msg: response.data?.msg ?? '请求失败',
      code: response.status,
      result: response.data?.result,
    };
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      return {
        success: false,
        msg: error?.response?.data?.msg ?? '请求失败',
        code: error?.response?.status ?? 500,
        result: error?.response?.data?.result,
      };
    }
    return {
      success: false,
      msg: '网络错误，请检查网络连接',
      code: 500,
      result: 'ERROR',
    };
  }
}
